import { useI18n } from "#hooks/use-i18n"
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  Dialog as DialogPrimitive,
  DialogTitle,
} from "../components/ui/dialog"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../components/ui/drawer"
import { useDesktop } from "../hooks/use-desktop"
import { cn } from "../lib/utils"
import { Button } from "./ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip"

interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  children: React.ReactNode
  footer?: React.ReactNode
  size?: "sm" | "md" | "lg" | "xl"
  className?: string
}

interface DialogActionProps {
  onCancel?: () => void
  onConfirm?: () => void
  confirmText?: string
  cancelText?: string
  confirmVariant?: "default" | "secondary" | "outline" | "ghost" | "destructive"
  cancelVariant?: "default" | "secondary" | "outline" | "ghost"
  isLoading?: boolean
  isConfirmDisabled?: boolean
  confirmTooltip?: string
}

const sizeClasses = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
}

const Dialog: React.FC<DialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
  className,
}) => {
  const isDesktop = useDesktop()

  const titleElement = isDesktop ? (
    <DialogTitle>{title}</DialogTitle>
  ) : (
    <DrawerTitle>{title}</DrawerTitle>
  )

  const descriptionElement = description ? (
    isDesktop ? (
      <DialogDescription>{description}</DialogDescription>
    ) : (
      <DrawerDescription>{description}</DrawerDescription>
    )
  ) : null

  const header = (
    <>
      {titleElement}
      {descriptionElement}
    </>
  )

  const headerWrapper = isDesktop ? (
    <DialogHeader>{header}</DialogHeader>
  ) : (
    <DrawerHeader className="text-left flex-shrink-0">{header}</DrawerHeader>
  )

  const childrenWrapper = (
    <div className={cn("overflow-y-auto flex-1 min-h-0", isDesktop ? "py-4 px-1" : "px-4 pb-4")}>
      {children}
    </div>
  )

  const footerElement = footer ? (
    isDesktop ? (
      <DialogFooter className="flex-shrink-0">{footer}</DialogFooter>
    ) : (
      <DrawerFooter className="flex-shrink-0">{footer}</DrawerFooter>
    )
  ) : null

  const content = (
    <>
      {headerWrapper}
      {childrenWrapper}
      {footerElement}
    </>
  )

  if (isDesktop) {
    return (
      <DialogPrimitive open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(sizeClasses[size], "max-h-[90vh] flex flex-col", className)}
          showCloseButton={true}
        >
          {content}
        </DialogContent>
      </DialogPrimitive>
    )
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="flex flex-col max-h-[96vh]">{content}</DrawerContent>
    </Drawer>
  )
}

const DialogActions: React.FC<DialogActionProps> = ({
  onCancel,
  onConfirm,
  confirmText,
  cancelText,
  confirmVariant = "ghost",
  cancelVariant = "outline",
  isLoading = false,
  isConfirmDisabled = false,
  confirmTooltip,
}) => {
  const { t } = useI18n()
  const isDesktop = useDesktop()
  const defaultConfirmText = confirmText || t("components.dialog.confirm")
  const defaultCancelText = cancelText || t("components.dialog.cancel")

  const cancelButton = onCancel ? (
    <Button type="button" onClick={onCancel} variant={cancelVariant} disabled={isLoading}>
      {defaultCancelText}
    </Button>
  ) : null

  const confirmButtonContent = (
    <Button
      type="button"
      onClick={onConfirm}
      variant={confirmVariant}
      disabled={isLoading || isConfirmDisabled}
    >
      {isLoading ? t("components.dialog.loading") : defaultConfirmText}
    </Button>
  )

  const confirmButton = onConfirm ? (
    confirmTooltip ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{confirmButtonContent}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{confirmTooltip}</p>
        </TooltipContent>
      </Tooltip>
    ) : (
      confirmButtonContent
    )
  ) : null

  const actionsContent = (
    <div className="flex justify-end gap-3">
      {cancelButton}
      {confirmButton}
    </div>
  )

  if (!isDesktop && onCancel) {
    return (
      <div className="flex justify-end gap-3">
        <DrawerClose asChild>{cancelButton}</DrawerClose>
        {confirmButton}
      </div>
    )
  }

  return actionsContent
}

export { Dialog, DialogActions }
